import cn from 'classnames';
import React, {forwardRef} from 'react';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  value: string; // input MUST receive string
  disableAutoFocus?: boolean;
  onValueChange?: (value: string) => void;
  onChangeFormatter?: (value: string) => string;
  className?: string;
  labelClassName?: string;
  leftElement?: React.ReactNode;
}

const InputField = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      required,
      disabled,
      value,
      disableAutoFocus,
      placeholder,
      type = 'text',
      onValueChange,
      onChangeFormatter,
      className = 'bg-white',
      labelClassName = '',
      leftElement,
      ...rest
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;

      // apply formatting if provided
      if (onChangeFormatter) {
        val = onChangeFormatter(val);
      }

      // send value to parent
      onValueChange?.(val);
    };

    return (
      <div className={`w-full my-1 ${disabled && 'opacity-50'}`}>
        {/* Label */}
        <label
          htmlFor={name}
          className={cn(
            'block py-2 text-md font-medium text-gray-800',
            labelClassName,
          )}
        >
          {label}
          {required && <span className="mr-1 text-lg text-red-600">*</span>}
        </label>

        {/* Input wrapper */}
        <div className="relative w-full">
          <input
            ref={ref}
            id={name}
            name={name}
            type={type}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            onChange={handleChange}
            className={cn(
              'w-full rounded-lg border-[1px] border-gray-500 px-3 py-2',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-1',
              'placeholder:text-sm placeholder:text-gray-400',
              {
                'bg-gray-100 pointer-events-none opacity-60': disabled,

                'focus:outline-none focus:ring-0 focus:ring-gray-500':
                  disableAutoFocus,

                // Reserve space for the left element.
                'pl-20': leftElement,
              },
              className,
            )}
            autoFocus={rest.autoFocus && !disableAutoFocus}
            {...rest}
          />

          {leftElement && (
            <div
              className={cn(
                'pointer-events-none absolute inset-y-0 left-0',
                'flex items-center',
                'max-w-[40%] overflow-hidden',
                'px-3',
                'text-sm text-gray-600',
                'whitespace-nowrap text-ellipsis',
              )}
            >
              {leftElement}
            </div>
          )}
        </div>
      </div>
    );
  },
);

InputField.displayName = 'InputField';

export default InputField;
